/**
 * Fetch wrapper. Adds the auth bearer + base URL.
 *
 * Errors:
 *   - 401 clears the stored token and reloads to /login.
 *   - Any other non-2xx throws ApiError { status, body }.
 */

import { getToken, clearToken } from './auth.ts'

// The UI is served by the API worker itself (wrangler.toml [assets]), so
// production calls are same-origin (''). The two legacy hosts — the stale
// Pages deployment and localhost vite — still need absolute URLs.
const legacyPagesHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'contenthub.thelanyardlife.com' ||
    window.location.hostname.endsWith('.pages.dev'))
const API_BASE =
  import.meta.env.VITE_API_BASE ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : legacyPagesHost
      ? 'https://contenthub-api.thelanyardlife.com'
      : '')

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (res.status === 401) {
    clearToken()
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new ApiError('unauthorized', 401, null)
  }
  const isJson = res.headers.get('content-type')?.includes('application/json')
  const body = isJson ? await res.json() : await res.text()
  if (!res.ok) throw new ApiError(`${res.status} on ${path}`, res.status, body)
  return body as T
}

export { API_BASE }
